import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, signal, untracked } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsEditorStore } from '../../../services/boards-editor.store';
import { BoardsStore } from '../../../services/boards.store';
import { createBlankBoard } from '../../creation/create-blank-board';
import { injectBoardOpener } from '../../shared/board-opener';

export const BOARD_EMPTY_STATE_ERROR = 'The board could not be created. Try again.';
export const BOARD_EMPTY_STATE_OPEN_ERROR = 'The board was created but could not be opened. Try again.';

/**
 * The "No board selected" state: the exact Boards illustration on the native `NoContentStub`, a 14/24 medium caption and
 * the 32px secondary `Button2` of the Notes empty state (`notes-main-view.component.html:3-10`). "Create a new board" is the
 * prototype's `newBoard` then `openEditor(id, true)` (`BoardEmptyState.tsx`, `Boards.tsx:138,210`) on the native commands: the
 * rail's own blank-board creation (`createBlankBoard`, behind the same access gate), then the layout's guarded opener
 * (`BOARD_OPENER`), which opens that SAME saved board full-window and only then folds panels 1, 2 and 4. In the Dynamic Panel
 * the same opener hands the board to the page. One activation at a time; a failed creation leaves the button usable with
 * the error beside it. After creation the activation stays locked while the editor store reports the open as running; if the open
 * fails, the made board is kept and the button opens THAT board again.
 */
@Component({
  selector: 'app-board-empty-state',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoContentStubComponent, Button2Component, IconComponent],
  templateUrl: './board-empty-state.component.html',
})
export class BoardEmptyStateComponent {
  private readonly store = inject(BoardsStore);
  private readonly listStore = inject(BoardListStore, { optional: true });
  private readonly editor = inject(BoardsEditorStore);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly openBoard = injectBoardOpener();
  protected readonly creating = signal(false);
  protected readonly error = signal('');
  /** The board this activation made. Once it exists, every further click opens IT and never creates another. */
  protected readonly madeId = signal<string | null>(null);
  /** The guarded open of the made board is still running (the editor store's own `opening`, as Slides' open). */
  private readonly opening = computed(() => this.madeId() !== null && this.editor.opening() === this.madeId());
  protected readonly busy = computed(() => this.creating() || this.opening());
  private sawOpening = false;
  private locked = false;

  public constructor() {
    // The open ended (it is no longer `opening`) without the editor showing the made board: it was refused or failed. The board is
    // kept; the same button now opens it again.
    effect(() => {
      const opening = this.opening();
      const made = this.madeId();
      untracked(() => {
        if (opening) this.sawOpening = true;
        else if (this.sawOpening && made !== null) {
          this.sawOpening = false;
          if (this.editor.editorBoardId() !== made) this.error.set(BOARD_EMPTY_STATE_OPEN_ERROR);
        }
      });
    });
  }

  protected async create(): Promise<void> {
    if (this.locked || this.opening()) return;
    this.error.set('');
    const made = this.madeId();
    if (made !== null) {
      this.openBoard(made);
      return;
    }
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.locked = true;
    this.creating.set(true);
    try {
      const board = await createBlankBoard(this.store, this.listStore);
      if (this.destroyRef.destroyed) return;
      if (board) {
        this.madeId.set(board.id);
        this.openBoard(board.id);
      } else this.error.set(BOARD_EMPTY_STATE_ERROR);
    } catch {
      if (!this.destroyRef.destroyed) this.error.set(BOARD_EMPTY_STATE_ERROR);
    } finally {
      this.locked = false;
      if (!this.destroyRef.destroyed) this.creating.set(false);
    }
  }
}
